import DashboardLayout from '@/components/DashboardLayout'
import { useAuth } from '@/components/Layout'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Link, router } from '@inertiajs/react'
import {
  DollarSign,
  Calendar,
  Users,
  UserCheck,
  Briefcase,
  Plus,
  ArrowUpRight,
  ArrowDownRight,
  Clock,
  Package,
  CreditCard,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
} from 'lucide-react'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'

interface School {
  id: number
  name: string
}

interface RevenueMonth {
  month: string
  month_number: number
  year: number
  revenue: number
  expenses: number
  profit: number
}

interface PendingPayment {
  id: number
  student_subscription_id: number | null
  student_name: string
  package_name: string
  amount: number
  formatted_amount: string
  days_pending: number
  created_at: string
}

interface RecentSale {
  id: number
  student_name: string
  package_name: string
  amount: number
  formatted_amount: string
  payment_method: string
  paid_at: string
}

interface TopPackage {
  name: string
  sales: number
  revenue: number
}

interface WaveCoinStats {
  total_in_circulation: number
  total_in_circulation_reais: number
  expired_this_month: number
  discount_granted: number
  top_students: { name: string; balance: number }[]
}

interface DashboardProps {
  school: School
  selected_month: number
  selected_year: number
  stats: {
    total_students: number
    total_teachers: number
    total_managers: number
    total_revenue: number
    total_expenses: number
    net_profit: number
    lessons_today_booked: number
    lessons_today_available: number
    revenue_this_month: number
    expenses_this_month: number
    revenue_previous_month: number
    revenue_growth: number | null
    sales_this_month: number
    sales_previous_month: number
    pending_total: number
    pending_count: number
    transfer_pending_total: number
    transfer_pending_gross: number
    transfer_pending_count: number
    next_payout_date: string
    lessons_month_total: number
    lessons_month_booked: number
    occupancy_rate: number
  }
  revenue_by_month: RevenueMonth[]
  pending_payments: PendingPayment[]
  recent_sales: RecentSale[]
  top_packages: TopPackage[]
  wavecoin_stats: WaveCoinStats | null
}

const MONTH_NAMES = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
]

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
    value,
  )

// "2026-08-20" -> "20/08". Sem new Date() de proposito: data pura parseada como
// Date vira UTC e volta um dia atras em fuso negativo.
const formatShortDate = (isoDate: string) => {
  if (!isoDate) return '-'
  const [, month, day] = isoDate.split('-')
  return `${day}/${month}`
}

const paymentMethodLabel: Record<string, string> = {
  cash: 'Dinheiro',
  credit_card: 'Cartão de Crédito',
  debit_card: 'Cartão de Débito',
  pix: 'PIX',
  bank_transfer: 'Transferência',
}

export default function Dashboard({
  school,
  selected_month,
  selected_year,
  stats,
  revenue_by_month,
  pending_payments,
  recent_sales,
  top_packages,
  wavecoin_stats,
}: DashboardProps) {
  const { user } = useAuth()

  const navigateMonth = (direction: number) => {
    let newMonth = selected_month + direction
    let newYear = selected_year
    if (newMonth < 1) {
      newMonth = 12
      newYear--
    }
    if (newMonth > 12) {
      newMonth = 1
      newYear++
    }
    router.visit(`/dashboard?month=${newMonth}&year=${newYear}`, {
      preserveState: true,
    })
  }

  return (
    <DashboardLayout schoolName={school?.name}>
      <div className="p-4 md:p-8 space-y-6">
        {/* Header + Month Selector */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h2 className="text-3xl font-bold tracking-tight">Dashboard</h2>
            <p className="text-muted-foreground">
              Bem-vindo, {user?.email?.split('@')[0]}!
            </p>
          </div>
          <div className="flex items-center gap-2 bg-muted rounded-lg p-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => navigateMonth(-1)}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm font-medium min-w-[120px] text-center">
              {MONTH_NAMES[selected_month - 1]} {selected_year}
            </span>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => navigateMonth(1)}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Main Stats */}
        <div className="grid gap-4 grid-cols-2 lg:grid-cols-3">
          {/* Receita do Mes */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Entradas do Mes
              </CardTitle>
              <ArrowUpRight className="h-4 w-4 text-green-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">
                {formatCurrency(stats.revenue_this_month)}
              </div>
              {stats.revenue_growth !== null ? (
                <p
                  className={`text-xs flex items-center gap-1 ${stats.revenue_growth >= 0 ? 'text-green-600' : 'text-red-600'}`}
                >
                  {stats.revenue_growth >= 0 ? (
                    <ArrowUpRight className="h-3 w-3" />
                  ) : (
                    <ArrowDownRight className="h-3 w-3" />
                  )}
                  {stats.revenue_growth >= 0 ? '+' : ''}
                  {stats.revenue_growth}% vs mes anterior
                </p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Sem dados do mes anterior
                </p>
              )}
            </CardContent>
          </Card>

          {/* Despesas do Mes */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Saidas do Mes
              </CardTitle>
              <ArrowDownRight className="h-4 w-4 text-red-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">
                {formatCurrency(stats.expenses_this_month)}
              </div>
              <p className="text-xs text-muted-foreground">
                Professores + despesas manuais
              </p>
            </CardContent>
          </Card>

          {/* Saldo do Mes */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Saldo do Mes
              </CardTitle>
              <DollarSign className="h-4 w-4 text-blue-500" />
            </CardHeader>
            <CardContent>
              <div
                className={`text-2xl font-bold ${stats.revenue_this_month - stats.expenses_this_month >= 0 ? 'text-blue-600' : 'text-red-600'}`}
              >
                {formatCurrency(
                  stats.revenue_this_month - stats.expenses_this_month,
                )}
              </div>
              <p className="text-xs text-muted-foreground">Entradas - Saidas</p>
            </CardContent>
          </Card>
        </div>

        {/* Secondary Stats */}
        <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
          {/* Vendas do Mes */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Vendas do Mes
              </CardTitle>
              <CreditCard className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.sales_this_month}</div>
              <p className="text-xs text-muted-foreground">
                {stats.sales_previous_month} no mes anterior
              </p>
            </CardContent>
          </Card>

          {/* Pagamentos Pendentes */}
          <Card
            className={
              stats.pending_count > 0
                ? 'border-amber-200 dark:border-amber-800'
                : ''
            }
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Pgtos Pendentes
              </CardTitle>
              <Clock className="h-4 w-4 text-amber-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-amber-600">
                {formatCurrency(stats.pending_total)}
              </div>
              <p className="text-xs text-muted-foreground">
                {stats.pending_count} aguardando
              </p>
            </CardContent>
          </Card>

          {/* A Repassar — valor LIQUIDO (o que a escola recebe); o bruto vai na legenda */}
          <Link
            href="/payouts"
            className="block rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <Card
              className={`h-full transition-colors hover:bg-muted/50 ${stats.transfer_pending_count > 0 ? 'border-blue-200 dark:border-blue-800' : ''}`}
            >
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  A Repassar
                </CardTitle>
                <ArrowUpRight className="h-4 w-4 text-blue-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-blue-600">
                  {formatCurrency(stats.transfer_pending_total)}
                </div>
                <p className="text-xs text-muted-foreground">
                  {stats.transfer_pending_count} via app &middot;{' '}
                  {formatCurrency(stats.transfer_pending_gross)} bruto
                </p>
                <p className="text-xs text-blue-600 mt-1">
                  Proximo repasse em {formatShortDate(stats.next_payout_date)}
                </p>
              </CardContent>
            </Card>
          </Link>

          {/* Aulas Hoje */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Aulas Hoje</CardTitle>
              <Calendar className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-primary">
                {stats.lessons_today_booked}
              </div>
              <p className="text-xs text-muted-foreground">
                {stats.lessons_today_booked === 1
                  ? 'Aula agendada'
                  : 'Aulas agendadas'}{' '}
                &middot; {stats.lessons_today_available}{' '}
                {stats.lessons_today_available === 1
                  ? 'vaga livre'
                  : 'vagas livres'}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Charts + Sidebar */}
        <div className="grid gap-4 lg:grid-cols-3">
          {/* Revenue Chart */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="text-base">
                Receitas, Despesas e Lucro
              </CardTitle>
              <CardDescription>Ultimos 6 meses</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[280px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={revenue_by_month}>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      className="stroke-muted"
                    />
                    <XAxis
                      dataKey="month"
                      className="text-xs"
                      tick={{ fontSize: 12 }}
                    />
                    <YAxis
                      className="text-xs"
                      tick={{ fontSize: 12 }}
                      tickFormatter={(v) => formatCurrency(v)}
                    />
                    <Tooltip
                      formatter={(value: any) => [
                        formatCurrency(Number(value)),
                      ]}
                      contentStyle={{ borderRadius: '8px', fontSize: '13px' }}
                    />
                    <Legend />
                    <Bar
                      dataKey="revenue"
                      fill="#16a34a"
                      name="Receita"
                      radius={[4, 4, 0, 0]}
                    />
                    <Bar
                      dataKey="expenses"
                      fill="#dc2626"
                      name="Despesas"
                      radius={[4, 4, 0, 0]}
                    />
                    <Bar
                      dataKey="profit"
                      fill="#2563eb"
                      name="Lucro"
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Right sidebar stats */}
          <div className="space-y-4">
            {/* Resumo Financeiro */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">
                  Resumo Financeiro
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">
                    Entradas
                  </span>
                  <span className="text-sm font-semibold text-green-600">
                    {formatCurrency(stats.total_revenue)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Saidas</span>
                  <span className="text-sm font-semibold text-red-600">
                    {formatCurrency(stats.total_expenses)}
                  </span>
                </div>
                <div className="border-t pt-2 flex items-center justify-between">
                  <span className="text-sm font-medium">Saldo</span>
                  <span
                    className={`text-lg font-bold ${stats.net_profit >= 0 ? 'text-green-600' : 'text-red-600'}`}
                  >
                    {formatCurrency(stats.net_profit)}
                  </span>
                </div>
              </CardContent>
            </Card>

            {/* Ocupação */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">
                  Taxa de Ocupação
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {stats.occupancy_rate}%
                </div>
                <p className="text-xs text-muted-foreground">
                  {stats.lessons_month_booked} de {stats.lessons_month_total}{' '}
                  aulas no mês
                </p>
                <div className="mt-2 h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary rounded-full transition-all"
                    style={{ width: `${Math.min(stats.occupancy_rate, 100)}%` }}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Equipe */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">
                  Equipe & Alunos
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="flex items-center gap-2 text-muted-foreground">
                    <Users className="h-3.5 w-3.5" /> Alunos
                  </span>
                  <span className="font-medium">{stats.total_students}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="flex items-center gap-2 text-muted-foreground">
                    <UserCheck className="h-3.5 w-3.5" /> Professores
                  </span>
                  <span className="font-medium">{stats.total_teachers}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="flex items-center gap-2 text-muted-foreground">
                    <Briefcase className="h-3.5 w-3.5" /> Gerentes
                  </span>
                  <span className="font-medium">{stats.total_managers}</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Tables row */}
        <div className="grid gap-4 lg:grid-cols-2">
          {/* Vendas Recentes */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Vendas Recentes</CardTitle>
              <CardDescription>Últimos pagamentos confirmados</CardDescription>
            </CardHeader>
            <CardContent>
              {recent_sales.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  Nenhuma venda registrada.
                </p>
              ) : (
                <div className="space-y-3 max-h-[300px] overflow-y-auto pr-1">
                  {recent_sales.map((sale) => (
                    <div
                      key={sale.id}
                      className="flex items-center justify-between gap-2"
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">
                          {sale.student_name}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {sale.package_name} ·{' '}
                          {paymentMethodLabel[sale.payment_method] ||
                            sale.payment_method}
                        </p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-sm font-medium text-green-700 dark:text-green-400">
                          {sale.formatted_amount}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {sale.paid_at}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Pagamentos Pendentes */}
          <Card
            className={
              pending_payments.length > 0
                ? 'border-amber-200 dark:border-amber-800'
                : ''
            }
          >
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                Pagamentos Pendentes
                {pending_payments.length > 0 && (
                  <span className="text-xs bg-amber-100 dark:bg-amber-900 text-amber-700 dark:text-amber-300 px-2 py-0.5 rounded-full">
                    {stats.pending_count}
                  </span>
                )}
              </CardTitle>
              <CardDescription>Aguardando confirmação</CardDescription>
            </CardHeader>
            <CardContent>
              {pending_payments.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  Nenhum pagamento pendente.
                </p>
              ) : (
                <div className="space-y-3 max-h-[300px] overflow-y-auto pr-1">
                  {pending_payments.map((p) => (
                    <div
                      key={p.id}
                      className="flex items-center justify-between gap-2 rounded-md p-2 -mx-2 cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={() =>
                        p.student_subscription_id &&
                        router.visit(
                          `/student_subscriptions/${p.student_subscription_id}/edit`,
                        )
                      }
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">
                          {p.student_name}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {p.package_name}
                        </p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-sm font-medium text-amber-700 dark:text-amber-400">
                          {p.formatted_amount}
                        </p>
                        <p className="text-xs text-muted-foreground flex items-center gap-1 justify-end">
                          <AlertCircle className="h-3 w-3" />
                          {p.days_pending}d pendente
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Top Packages */}
        {top_packages.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Pacotes Mais Vendidos</CardTitle>
              <CardDescription>Ranking por número de vendas</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 max-h-[300px] overflow-y-auto pr-1">
                {top_packages.map((pkg, i) => (
                  <div key={pkg.name} className="flex items-center gap-3">
                    <span className="text-lg font-bold text-muted-foreground w-6">
                      {i + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{pkg.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {pkg.sales} vendas
                      </p>
                    </div>
                    <p className="text-sm font-medium text-green-700 dark:text-green-400 shrink-0">
                      {formatCurrency(pkg.revenue)}
                    </p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* WaveCoins Stats */}
        {wavecoin_stats && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">WaveCoins</CardTitle>
              <CardDescription>Metricas do sistema de cashback</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 grid-cols-2 lg:grid-cols-4 mb-4">
                <div>
                  <p className="text-sm text-muted-foreground">Em Circulacao</p>
                  <p className="text-xl font-bold text-primary">
                    {wavecoin_stats.total_in_circulation.toLocaleString()} coins
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {formatCurrency(wavecoin_stats.total_in_circulation_reais)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">
                    Expirados (mes)
                  </p>
                  <p className="text-xl font-bold text-amber-600">
                    {wavecoin_stats.expired_this_month.toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">
                    Desconto Concedido
                  </p>
                  <p className="text-xl font-bold text-green-600">
                    {formatCurrency(wavecoin_stats.discount_granted)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">
                    Alunos com Saldo
                  </p>
                  <p className="text-xl font-bold">
                    {wavecoin_stats.top_students.length}
                  </p>
                </div>
              </div>
              {wavecoin_stats.top_students.length > 0 && (
                <div>
                  <p className="text-sm font-medium mb-2">Top alunos</p>
                  <div className="space-y-2">
                    {wavecoin_stats.top_students.map((s, i) => (
                      <div
                        key={i}
                        className="flex items-center justify-between text-sm"
                      >
                        <span className="text-muted-foreground">
                          {i + 1}. {s.name}
                        </span>
                        <span className="font-medium text-primary">
                          {s.balance.toLocaleString()} coins
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Ações Rápidas</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-3">
            <Button onClick={() => router.visit('/lessons/new')}>
              <Plus className="w-4 h-4 mr-2" />
              Agendar Aula
            </Button>
            <Button onClick={() => router.visit('/lessons')} variant="outline">
              <Calendar className="w-4 h-4 mr-2" />
              Ver Agenda
            </Button>
            <Button
              onClick={() => router.visit('/financial')}
              variant="outline"
            >
              <DollarSign className="w-4 h-4 mr-2" />
              Financeiro
            </Button>
            {user?.role === 'owner' && (
              <>
                <Button
                  onClick={() => router.visit('/students')}
                  variant="outline"
                >
                  <Users className="w-4 h-4 mr-2" />
                  Gerenciar Alunos
                </Button>
                <Button
                  onClick={() => router.visit('/lesson_packages')}
                  variant="outline"
                >
                  <Package className="w-4 h-4 mr-2" />
                  Pacotes de Aulas
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  )
}
